import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { AUTH_EXPIRED_EVENT } from '../services/api';
import apiService from '../services/api';

export interface GoogleUser {
  sub: string; // Google's unique user ID
  email: string;
  name: string;
  picture: string;
  email_verified: boolean;
}

export interface AuthContextType {
  user: GoogleUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  sessionExpired: boolean;
  signIn: (credential: string) => Promise<void>;
  signOut: () => void;
  clearSessionExpired: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

// Valid Google issuer values
const GOOGLE_ISSUERS = ['accounts.google.com', 'https://accounts.google.com'];

// Google's tokeninfo endpoint for proper token verification
const GOOGLE_TOKENINFO_URL = 'https://oauth2.googleapis.com/tokeninfo';

// Storage keys
const STORAGE_KEY = 'google_auth_token';
const SESSION_TOKEN_KEY = 'session_auth_token';
const TOKEN_EXPIRY_KEY = 'google_token_expiry';

// Helper function to parse email_verified field (can be boolean or string)
const parseEmailVerified = (value: unknown): boolean => {
  return value === 'true' || value === true;
};

// Decode JWT to extract expiration time and user info without verification
const decodeJWT = (token: string): { exp?: number; sub?: string; email?: string; name?: string; picture?: string; email_verified?: boolean | string } | null => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map((c) => {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decoding JWT:', error);
    return null;
  }
};

// Verify JWT token with Google's tokeninfo endpoint
const verifyTokenWithGoogle = async (token: string): Promise<GoogleUser | null> => {
  try {
    const response = await fetch(`${GOOGLE_TOKENINFO_URL}?id_token=${token}`);

    if (!response.ok) {
      console.error('Token verification failed with status:', response.status);
      return null;
    }

    const tokenInfo = await response.json();

    // Validate issuer
    if (!tokenInfo.iss || !GOOGLE_ISSUERS.includes(tokenInfo.iss)) {
      console.error('Invalid token: untrusted issuer', tokenInfo.iss);
      return null;
    }

    // Validate required fields
    if (!tokenInfo.sub || !tokenInfo.email) {
      console.error('Invalid token: missing required fields');
      return null;
    }

    // Google's tokeninfo endpoint already validates:
    // - Signature
    // - Expiration
    // - Audience (if needed)

    return {
      sub: tokenInfo.sub,
      email: tokenInfo.email,
      name: tokenInfo.name || '',
      picture: tokenInfo.picture || '',
      email_verified: parseEmailVerified(tokenInfo.email_verified),
    };
  } catch (error) {
    console.error('Error verifying token with Google:', error);
    return null;
  }
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<GoogleUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);

  // Load and verify session token from localStorage on mount
  useEffect(() => {
    const verifyStoredToken = async () => {
      const sessionToken = localStorage.getItem(SESSION_TOKEN_KEY);
      const expiryStr = localStorage.getItem(TOKEN_EXPIRY_KEY);

      if (sessionToken && expiryStr) {
        const expiry = parseInt(expiryStr, 10);
        const now = Date.now();

        // Check if token is expired
        if (expiry <= now) {
          console.warn('Stored session token is expired');
          localStorage.removeItem(SESSION_TOKEN_KEY);
          localStorage.removeItem(STORAGE_KEY);
          localStorage.removeItem(TOKEN_EXPIRY_KEY);
          setIsLoading(false);
          return;
        }

        try {
          // Decode session token to get user info
          const decoded = decodeJWT(sessionToken);
          if (decoded && decoded.sub) {
            // Session token only contains user ID. Try to get full user info from stored Google token
            // to avoid calling Google API on every page load. This improves performance.
            const storedGoogleToken = localStorage.getItem(STORAGE_KEY);
            if (storedGoogleToken) {
              // Try to decode stored Google token for user info (no verification needed - just data)
              const googleDecoded = decodeJWT(storedGoogleToken);
              if (googleDecoded && googleDecoded.email) {
                // Use cached user info from Google token
                setUser({
                  sub: decoded.sub,
                  email: googleDecoded.email || '',
                  name: googleDecoded.name || '',
                  picture: googleDecoded.picture || '',
                  email_verified: parseEmailVerified(googleDecoded.email_verified),
                });
              } else {
                // Fallback: minimal user from session token
                setUser({
                  sub: decoded.sub,
                  email: decoded.email || '',
                  name: decoded.name || '',
                  picture: decoded.picture || '',
                  email_verified: parseEmailVerified(decoded.email_verified),
                });
              }
            } else {
              // No Google token, use session token data (minimal user)
              setUser({
                sub: decoded.sub,
                email: decoded.email || '',
                name: decoded.name || '',
                picture: decoded.picture || '',
                email_verified: parseEmailVerified(decoded.email_verified),
              });
            }
          } else {
            console.warn('Invalid session token format');
            localStorage.removeItem(SESSION_TOKEN_KEY);
            localStorage.removeItem(STORAGE_KEY);
            localStorage.removeItem(TOKEN_EXPIRY_KEY);
          }
        } catch (error) {
          console.error('Error verifying stored session token:', error);
          localStorage.removeItem(SESSION_TOKEN_KEY);
          localStorage.removeItem(STORAGE_KEY);
          localStorage.removeItem(TOKEN_EXPIRY_KEY);
        }
      }
      setIsLoading(false);
    };

    verifyStoredToken();
  }, []); // Only run once on mount

  const signIn = useCallback(async (credential: string) => {
    try {
      // Verify Google ID token and get user info
      const verifiedUser = await verifyTokenWithGoogle(credential);

      if (verifiedUser) {
        // Exchange Google token for session token
        try {
          const response = await apiService.exchangeGoogleToken(credential);
          const { token: sessionToken, expires_in } = response.data;

          // Calculate expiry time
          const expiryTime = Date.now() + expires_in * 1000;

          // Store session token and user info
          localStorage.setItem(SESSION_TOKEN_KEY, sessionToken);
          localStorage.setItem(STORAGE_KEY, credential); // Keep Google token for user info
          localStorage.setItem(TOKEN_EXPIRY_KEY, expiryTime.toString());

          setUser(verifiedUser);
          setSessionExpired(false);

          console.log('User signed in with session token');
        } catch (error) {
          console.error('Error exchanging Google token for session token:', error);
          // Fall back to using Google token directly
          const decoded = decodeJWT(credential);
          if (!decoded || !decoded.exp) {
            console.error('Failed to decode token or token missing expiration');
            return;
          }

          localStorage.setItem(STORAGE_KEY, credential);
          const expiryTime = decoded.exp * 1000;
          localStorage.setItem(TOKEN_EXPIRY_KEY, expiryTime.toString());

          setUser(verifiedUser);
          setSessionExpired(false);
        }
      } else {
        console.error('Token verification failed');
      }
    } catch (error) {
      console.error('Error during sign in:', error);
    }
  }, []);

  const signOut = useCallback(() => {
    setUser(null);
    setSessionExpired(false);
    localStorage.removeItem(SESSION_TOKEN_KEY);
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(TOKEN_EXPIRY_KEY);
  }, []);

  const clearSessionExpired = useCallback(() => {
    setSessionExpired(false);
  }, []);

  // Listen for auth expired events from API interceptor
  useEffect(() => {
    const handleAuthExpired = () => {
      console.warn('Auth token expired, signing out');
      setUser(null);
      setSessionExpired(true);
      localStorage.removeItem(SESSION_TOKEN_KEY);
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(TOKEN_EXPIRY_KEY);
    };

    window.addEventListener(AUTH_EXPIRED_EVENT, handleAuthExpired);
    return () => {
      window.removeEventListener(AUTH_EXPIRED_EVENT, handleAuthExpired);
    };
  }, []);

  // Detect manual token deletion or expiry change from localStorage
  // This checks when window gains focus (user clicks back into the app)
  useEffect(() => {
    const checkTokenExistsAndValid = () => {
      const sessionToken = localStorage.getItem(SESSION_TOKEN_KEY);
      const expiryStr = localStorage.getItem(TOKEN_EXPIRY_KEY);

      if (user) {
        // Check if session token was deleted
        if (!sessionToken) {
          console.warn('Session token manually deleted, signing out');
          setUser(null);
          setSessionExpired(true);
          localStorage.removeItem(SESSION_TOKEN_KEY);
          localStorage.removeItem(STORAGE_KEY);
          localStorage.removeItem(TOKEN_EXPIRY_KEY);
          return;
        }

        // Check if expiry was changed to past timestamp
        if (expiryStr) {
          const expiry = parseInt(expiryStr, 10);
          const now = Date.now();
          if (expiry <= now) {
            console.warn('Session token expiry changed to past timestamp, signing out');
            setUser(null);
            setSessionExpired(true);
            localStorage.removeItem(SESSION_TOKEN_KEY);
            localStorage.removeItem(STORAGE_KEY);
            localStorage.removeItem(TOKEN_EXPIRY_KEY);
          }
        }
      }
    };

    // Check when window gains focus
    window.addEventListener('focus', checkTokenExistsAndValid);

    return () => {
      window.removeEventListener('focus', checkTokenExistsAndValid);
    };
  }, [user]);

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    sessionExpired,
    signIn,
    signOut,
    clearSessionExpired,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
